/**
 * SkippingStrategy - Detection strategy for jump rope/skipping exercise
 *
 * Uses MoveNet pose detection to count skipping reps.
 * Draws skeleton, keypoints, ground line, and ankle height indicators.
 */
import { BaseExerciseStrategy } from './BaseExerciseStrategy.js';
import { registerStrategy } from './exerciseStrategyRegistry.js';
import {
  drawKeypoints,
  drawSkeleton,
  drawGroundLine,
  drawAnkleHeights,
  drawStatusOverlay,
  drawBoundingBox
} from '../utils/drawUtils.js';

export class SkippingStrategy extends BaseExerciseStrategy {
  static get id() {
    return 'skipping';
  }

  static get displayName() {
    return 'Skipping';
  }

  static get detectorType() {
    return 'POSE';
  }

  static get counterId() {
    return 'skipping';
  }

  static get audioPreset() {
    return 'skipping';
  }

  getLabels() {
    return {
      title: 'Skipping Counter',
      repNoun: 'Reps Completed',
      cameraTip: 'Tip: Stand back so your full body is visible in the camera for accurate counting.',
      beepMessage: 'Every time I skipped correctly the beep sound will play',
      loadingModel: 'Loading pose detection and XGBoost ML model...'
    };
  }

  getInitialStatus() {
    return { calibrating: true };
  }

  getInitialDebugState() {
    return {};
  }

  /**
   * Run pose detection
   */
  async runDetection(video) {
    return await this.detect(video);
  }

  /**
   * Build frame data from poses
   */
  buildFrameData(poses, timestamp) {
    return {
      timestamp,
      keypoints: poses?.length > 0 ? poses[0].keypoints.map(kp => ({
        x: kp.x,
        y: kp.y,
        score: kp.score,
        name: kp.name
      })) : null
    };
  }


  /**
   * Extract status from counter state (not frame data for skipping)
   */
  getStatusFromFrameData(frameData) {
    const counterState = this.getCounterState();
    if (!counterState) return null;
    return {
      calibrating: counterState.isCalibrating,
      isInAir: counterState.isInAir
    };
  }

  /**
   * Draw pose visualizations
   */
  drawFrame(ctx, video, poses, frameData, currentRepCount) {
    if (!poses?.length) return;

    const pose = poses[0];
    const counterState = this.getCounterState();
    const canvas = this.canvasRef.current;

    // Draw visualizations
    drawBoundingBox(pose.keypoints, ctx);
    drawSkeleton(pose.keypoints, ctx);
    drawKeypoints(pose.keypoints, ctx);

    if (counterState) {
      drawGroundLine(counterState.groundThreshold, canvas.width, ctx);
      drawAnkleHeights(pose.keypoints, counterState.groundThreshold, ctx);
      drawStatusOverlay(counterState, ctx);
    }
  }

  convertToFrameData(poses, timestamp) {
    if (!poses?.length) return null;

    const counterState = this.getCounterState();
    return {
      timestamp,
      keypoints: poses[0].keypoints.map(kp => ({
        x: kp.x,
        y: kp.y,
        score: kp.score,
        name: kp.name
      })),
      repCount: counterState?.repCount || 0,
      isInAir: counterState?.isInAir || false,
      isCalibrating: counterState?.isCalibrating || false,
      groundThreshold: counterState?.groundThreshold || 0
    };
  }

  renderStatusDisplay(status, labels) {
    // Skipping uses the canvas overlay for status (calibration message)
    // No separate React component needed
    return null;
  }

  getModelVersionsDisplay(modelVersions) {
    // Handle both "xgboost-jump-model-{hash}.json" and "xgboost-jump-model.json" formats
    let xgboostDigest = modelVersions?.xgboost?.skippingModel?.replace('.json', '').replace('xgboost-jump-model-', '').replace('xgboost-jump-model', '') || '?';
    if (xgboostDigest === '') xgboostDigest = 'default';
    return `Engine: ${this.engineDisplayLabel()} - MoveNet: Lightning - XGBoost: ${xgboostDigest}`;
  }

  renderDebugPanel(debugState) {
    // No debug panel for skipping
    return null;
  }

  supportsRecording() {
    return true;
  }

  initializeMovementLog(maxFrames) {
    // Pose detection uses dynamic array
    return [];
  }

  sliceMovementLog(log, frameCount) {
    return log.slice(0, frameCount || log.length);
  }
}

// Register this strategy
registerStrategy(SkippingStrategy);
